import { Box, Button, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { loginVrSession, logout } from '../../../../auth/auth';
import { ENV_WARNING_BAR_HEIGHT } from '../../../../base/components/EnviromentWarningBar/EnviromentWarningBar';
import { Flex } from '../../../../base/components/Flex';
import ScreenMirrorWorkaround from '../../../../base/components/ScreenMirrorWorkaround/ScreenMirrorWorkaround';
import { useTranslationPrefix } from '../../../../hooks/useTranslationPrefix';
import { HttpError } from '../../../../lib/errors';
import { Loader } from '../../../components/Loader';
import { useSessionData } from '../../Sessions/useSessionData';

enum PageState {
  LOADING = 'LOADING',
  STREAM = 'STREAM',
  ERROR = 'ERROR',
}

interface Props {
  sessionId: string;
  onNext: () => void;
  onLaunchAgain: () => void;
}

const QrStreamingStep = ({ sessionId, onNext, onLaunchAgain }: Props) => {
  const { _t } = useTranslationPrefix('LaunchPlaylistQrPageNew');
  const videElementRef = useRef<HTMLDivElement>(null);
  const [pageState, setPageState] = useState<PageState>(PageState.LOADING);

  useEffect(() => {
    if (!sessionId) {
      return;
    }
    setPageState(PageState.LOADING);
    loginVrSession(sessionId)
      .then(() => {
        setPageState(PageState.STREAM);
      })
      .catch((e: HttpError) => {
        setPageState(PageState.ERROR);
        console.error('Login failed', e);
      });
  }, [sessionId, setPageState]);

  useEffect(() => {
    //logout when exiting this screen
    return () => {
      logout();
    };
  }, []);

  const { data, loading, endMutation, error } = useSessionData({
    vrSessionId: sessionId!,
    skip: pageState !== PageState.STREAM,
  });

  useEffect(() => {
    if (!!data?.session.endedAt) {
      onNext();
    }
  }, [!!data?.session.endedAt]);

  const handleEndSession = async () => {
    if (data?.session.vrDevice.id && !data.session.endedAt) {
      await endMutation({
        variables: {
          deviceIds: [data.session.vrDevice.id],
        },
      });
    }
    onNext();
  };

  let subtitle = _t('streamingTitle', 'View from app.');
  subtitle = subtitle ? `${subtitle}`.trim() : '';

  return (
    <Flex
      flexDirection="column"
      width="100%"
      minHeight="100%"
      alignItems="center"
      justifyContent="space-between"
      gap={1}
    >
      {subtitle && <Typography textAlign="center">{subtitle}</Typography>}

      {loading || pageState === PageState.LOADING ? (
        <Flex
          width="100%"
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Loader />
        </Flex>
      ) : pageState === PageState.STREAM ? (
        <>
          <Box
            id="vimeo-wraper"
            ref={videElementRef}
            sx={{
              flexGrow: 0,
              flexShrink: 0,
              width: '100%',
              maxHeight: `calc( 100dvh - ${
                subtitle ? "8rem" : "6rem"
              } - ${ENV_WARNING_BAR_HEIGHT} )`,
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                whiteSpace: "pre-wrap",
                padding: 2,
                margin: "auto",
                position: "fixed",
                top: "40%",
                left: "50%",
                maxWidth: "80dvw",
                transform: "translateX(-50%)",
              }}
            >
              {_t("streamFallback", "Training in progress...")}
            </Box>
            <ScreenMirrorWorkaround
              deviceId={data?.session.vrDevice.id || ''}
              style={{
                objectFit: 'contain',
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={2}
            width="100%"
            px={{ xs: 0, md: 3 }}
          >
            <Button
              id="launch-again"
              variant="text"
              sx={{ color: 'grey.300' }}
              onClick={onLaunchAgain}
            >
              {_t('launchAgain', 'Launch again')}
            </Button>
            <Button id="end-session" onClick={handleEndSession}>
              {_t('endSession', 'End session')}
            </Button>
          </Flex>
        </>
      ) : (
        <>
          <Flex width="100%" flexDirection="column" gap={4} alignItems="center">
            <Typography>
              {_t(
                'failedToLaunchApology',
                'We are sorry but something went wrong.',
              )}
            </Typography>
            <Button id="launch-again" onClick={onLaunchAgain}>
              {_t('launchAgain', 'Launch again')}
            </Button>
          </Flex>
          <Box></Box>
        </>
      )}
    </Flex>
  );
};

export default QrStreamingStep;
